import { ROUTES } from '@/routes/routeConfig';

import { useEffect, useMemo } from 'react';

import { useAuth0 } from '@auth0/auth0-react';
import { useGetActiveDPHQuery, useGetTenantsQuery } from '@features/tenants';
import { useGetUsersQuery } from '@features/users';
import { Alert, Box, Tab, Tabs, Typography } from '@mui/material';
import { Link, Outlet, useLocation, useNavigate, useParams } from 'react-router';

import { InlineLoader } from '@/components/common';

/**
 * Tenant Settings Content - Organization-specific tabs and content
 * Rendered within AccountSettingsLayout
 */
export const TenantSettings = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { orgId } = useParams<{ orgId: string }>();

  // Get user's tenants
  const { data: tenants, isLoading, error } = useGetTenantsQuery();

  // Sort tenants alphabetically
  const sortedTenants = useMemo(
    () =>
      tenants && tenants.length > 0
        ? [...tenants].sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
        : [],
    [tenants]
  );

  // Get current tenant from URL param (convert both to strings for comparison)
  const currentTenant =
    sortedTenants.find((t) => String(t.tenantId) === String(orgId)) || sortedTenants[0];

  // Check if DPH is enabled for current tenant (non-empty response means DPH is configured)
  const { data: dphData, isError: dphError } = useGetActiveDPHQuery(currentTenant?.tenantId ?? '', {
    skip: !currentTenant?.tenantId,
  });
  const isDPHEnabled = !dphError && !!dphData && Object.keys(dphData).length > 0;

  // Check if current user is admin in this tenant (DPH tab is admin-only)
  const { user: auth0User } = useAuth0();
  const { data: allUsers = [], isLoading: isLoadingUsers } = useGetUsersQuery();
  const tenantUsers = allUsers.filter(
    (u) => String(u.tenantid) === String(currentTenant?.tenantId)
  );
  const currentUserInTenant = tenantUsers.find((u) => u.username === auth0User?.email);
  const isCurrentUserAdmin = currentUserInTenant?.admin ?? false;

  // Redirect from /account-settings (no orgId) to themes
  useEffect(() => {
    if (location.pathname === ROUTES.ACCOUNT_SETTINGS && !isLoading) {
      navigate('/account-settings/themes', {
        replace: true,
      });
    }
  }, [location.pathname, isLoading, navigate]);

  // Redirect if no orgId in URL but we have tenants
  useEffect(() => {
    if (!orgId && sortedTenants.length > 0 && !isLoading) {
      navigate(`/account-settings/organizations/${sortedTenants[0].tenantId}/users`, {
        replace: true,
      });
    }
  }, [orgId, sortedTenants, isLoading, navigate]);

  // Redirect away from DPH route if DPH is not enabled OR user is not admin
  // Only redirect after users data has loaded to avoid false redirects
  useEffect(() => {
    if (
      currentTenant &&
      !isLoadingUsers &&
      (!isDPHEnabled || !isCurrentUserAdmin) &&
      location.pathname.includes('/data-processing-hours')
    ) {
      navigate(`/account-settings/organizations/${currentTenant.tenantId}/users`, {
        replace: true,
      });
    }
  }, [
    currentTenant,
    isDPHEnabled,
    isCurrentUserAdmin,
    isLoadingUsers,
    location.pathname,
    navigate,
  ]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
        <InlineLoader size={32} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Failed to load tenant information</Alert>
      </Box>
    );
  }

  // Determine active tab based on current route
  const getActiveTab = () => {
    if (location.pathname.includes('/users') && !location.pathname.includes('/projects/')) {
      return 0; // Organization Users
    }
    if (location.pathname.includes('/projects')) {
      return 1; // Project Users
    }
    if (
      location.pathname.includes('/data-processing-hours') &&
      isDPHEnabled &&
      isCurrentUserAdmin
    ) {
      return 2; // DPH (admin-only)
    }
    return 0;
  };

  if (!currentTenant) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Please select an organization from the sidebar</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Org Info Header */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="body2">
          Org Name: <strong>{currentTenant?.name}</strong>
        </Typography>
        <Typography variant="body2">
          Org Key: <strong>{currentTenant?.key}</strong>
        </Typography>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Tabs value={getActiveTab()} aria-label="tenant settings tabs">
          <Tab
            label="Organization Users"
            component={Link}
            to={`/account-settings/organizations/${currentTenant?.tenantId}/users`}
          />
          <Tab
            label="Project Users"
            component={Link}
            to={`/account-settings/organizations/${currentTenant?.tenantId}/projects`}
          />
          {isDPHEnabled && isCurrentUserAdmin && (
            <Tab
              label="Data Processing Hours"
              component={Link}
              to={`/account-settings/organizations/${currentTenant?.tenantId}/data-processing-hours`}
            />
          )}
        </Tabs>
      </Box>

      {/* Tab Content */}
      <Box sx={{ p: 3 }}>
        <Outlet context={{ tenantId: currentTenant?.tenantId, tenant: currentTenant }} />
      </Box>
    </Box>
  );
};
